import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import style from "./history.module.scss";
import styles from "./self-service.module.scss";
import { initialPaginationData } from "../utils/constants";

import Table, { Column } from "../components/table/Table";
import LayoutContext from "../context/layout-context";
import Modal, { ModalProps } from "../components/modal/Modal";
import { resources } from "../utils/resources";
import Button from "../components/button/Button";
import Input from "../components/input/Input";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import {
  ClassName,
  TaskCreate,
  TaskCreations,
  TaskExecute
} from "../utils/helpers/types";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import {
  createTask,
  deleteTask,
  excuteTasks,
  fetchClassNames,
  fetchInheritedModules,
  getTasks,
  updateTask
} from "../utils/helpers/data/task-creation";
import { getOptionsFromArray } from "../utils/helpers/type-conversions";
import Select, { Option } from "../components/select/Select";
import FileUploadModal from "./fileUploader";

const statusMap: Record<string, string> = {
  RUNNING: "success",
  HOLD: "warning"
  // NOT_RUNNING: "error"
};

const statusOptions: Option[] = [
  {
    label: "RUNNING",
    value: "RUNNING"
  },
  {
    label: "NOT RUNNING",
    value: "NOT_RUNNING"
  },
  {
    label: "HOLD",
    value: "HOLD"
  }
];

const TaskCreation: FunctionComponent = () => {
  const navigate = useNavigate();
  let timerRef: NodeJS.Timeout;
  const { notify } = useContext(LayoutContext);
  const [taskCreate] = useState<TaskCreations[]>([]);

  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [tasks, setTasks] = useState<TaskCreations[]>([]);
  const [loading, setLoading] = useState(false);
  const [taskOnView, setTaskOnView] = useState<TaskCreations | null>(null);

  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [taskOnEdit, setTaskOnEdit] = useState<TaskCreations | null>(null);

  const displayAddEditModal = (taskCreate: TaskCreations | null) => {
    setTaskOnEdit(taskCreate);
    setShowAddEditModal(true);
  };
  const [state, setState] = useState<string | undefined>(undefined);
  const [className, setClassName] = useState<string | undefined>(undefined);
  const [taskName, setTaskName] = useState("");
  const [classNamesOptions, setClassNamesOptions] = useState<Option[]>([]);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateTaskName, setGenerateTaskName] = useState("");
  const [selectedMonth, setSelectedMonth] = useState<number | undefined>(
    undefined
  );
  const [selectedYear, setSelectedYear] = useState<number | undefined>(
    undefined
  );
  const [selectedDays, setSelectedDays] = useState<number[]>([]);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadTaskClass, setUploadTaskClass] = useState("");
  const [uploadInheritedModule, setUploadInheritedModule] = useState("");
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

  const displayUploadModal = () => {
    setUploadTaskClass("");
    setUploadInheritedModule("");
    setFilesToUpload([]);
    setShowUploadModal(true);
  };

  const dismissUploadModal = () => {
    setUploadTaskClass("");
    setUploadInheritedModule("");
    setFilesToUpload([]);
    setShowUploadModal(false);
  };

  const displayGenerateModal = (taskName: string) => {
    setGenerateTaskName(taskName);
    setShowGenerateModal(true);
  };

  const dismissGenerateModal = () => {
    setGenerateTaskName("");
    setSelectedMonth(undefined);
    setSelectedYear(undefined);
    setShowGenerateModal(false);
  };

  const fetchTasks = async (
    pageNumber = pagination.pageNumber,
    pageSize = pagination.pageSize
  ) => {
    setLoading(true);
    const { data, error, message } = await getTasks(
      pageNumber,
      pageSize,
      state,
      taskName,
      className
    );
    setLoading(false);

    if (error || !data) {
      notify("error", `Unable to fetch tasks: ${message}`);
      return;
    }

    setTasks(data.records);
    setPagination({
      ...pagination,
      pageNumber,
      total: data.total
    });
  };

  const dismissAddEditModal = () => {
    setTaskOnEdit(null);
    setShowAddEditModal(false);
  };

  const handleConfigurationClick = (
    taskName: string | undefined,
    id: number | undefined
  ) => {
    if (taskName) {
      navigate(`/app-config?taskName=${taskName}&id=${id}`);
    } else {
      //
    }
  };

  const { confirm } = useContext(LayoutContext);

  const handleDeleteTasks = async (taskName: string) => {
    confirm({
      title: "Are you sure you want to delete this task?",
      body: "This action cannot be undone. Please confirm if you want to proceed.",
      onOk: async () => {
        setLoading(true);
        const { data, error, message } = await deleteTask(taskName);
        setLoading(false);

        if (error) {
          notify("error", `Unable to delete task: ${message}`);
          return;
        }

        notify("success", message || "Task deleted successfully");
        fetchTasks();
      }
    });
  };

  const handleHistoryClick = (
    taskName: string | undefined,
    id: number | undefined
  ) => {
    if (taskName) {
      navigate(`/task-history?taskName=${taskName}&id=${id}`);
    } else {
      console.log("Task name is not defined.");
    }
  };

  const handleFileHistoryClick = (taskName: string | undefined) => {
    if (taskName) {
      navigate(`/files?taskName=${taskName}`);
    }
  };

  const handleHoldClick = async (task: TaskCreations) => {
    if (!task.id) return;

    const partialTask: Partial<TaskCreate> = {
      taskName: task.taskName,
      className: task.className,
      intervalHour: task.intervalHour,
      state: "HOLD"
    };

    if (task.nextRunTime) {
      partialTask.nextRunTime = dayjs(task.nextRunTime).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      partialTask.nextRunTimee = dayjs(task.nextRunTime).format("HH:mm:ss");
      partialTask.nextRunDate = dayjs(task.nextRunTime).format("YYYY-MM-DD");
    }

    if (task.endTime) {
      partialTask.endTime = dayjs(task.endTime).format("YYYY-MM-DD HH:mm:ss");
      partialTask.endTimee = dayjs(task.endTime).format("HH:mm:ss");
      partialTask.endDate = dayjs(task.endTime).format("YYYY-MM-DD");
    }

    if (task.inheritedModule) {
      partialTask.inheritedModule = task.inheritedModule;
    }

    const updatedTask = partialTask as TaskCreate;

    const { error, message, data } = await updateTask(task.id, updatedTask);

    if (error) {
      const errorDetails = data ? `: ${data.join(", ")}` : "";
      notify("error", `Failed to hold task: ${message}${errorDetails}`);
    } else {
      notify("success", "Task is now on hold");
      fetchTasks();
    }
  };

  const handleUnholdClick = async (task: TaskCreations) => {
    if (!task.id) return;

    const partialTask: Partial<TaskCreate> = {
      taskName: task.taskName,
      className: task.className,
      intervalHour: task.intervalHour,
      state: "NOT_RUNNING"
    };

    if (task.nextRunTime) {
      partialTask.nextRunTime = dayjs(task.nextRunTime).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      partialTask.nextRunTimee = dayjs(task.nextRunTime).format("HH:mm:ss");
      partialTask.nextRunDate = dayjs(task.nextRunTime).format("YYYY-MM-DD");
    }

    if (task.endTime) {
      partialTask.endTime = dayjs(task.endTime).format("YYYY-MM-DD HH:mm:ss");
      partialTask.endTimee = dayjs(task.endTime).format("HH:mm:ss");
      partialTask.endDate = dayjs(task.endTime).format("YYYY-MM-DD");
    }

    if (task.inheritedModule) {
      partialTask.inheritedModule = task.inheritedModule;
    }

    const updatedTask = partialTask as TaskCreate;

    const { error, message } = await updateTask(task.id, updatedTask);

    if (error) {
      notify("error", `Failed to unhold task: ${message}`);
    } else {
      notify("success", "Updated Successfully");
      fetchTasks();
    }
  };

  const [resetFilter, setResetFilter] = useState("reset");
  const [debouncedTaskName, setDebouncedTaskName] = useState(taskName);
  const [timeoutValue, setTimeoutValue] = useState<NodeJS.Timeout>();

  useEffect(() => {
    if (timeoutValue) {
      clearTimeout(timeoutValue);
      console.log("cleared timeout handler - ", timeoutValue);
    }
    setTimeoutValue(setTimeout(() => setDebouncedTaskName(taskName), 400));
  }, [taskName]);

  useEffect(() => {
    if (debouncedTaskName) {
      console.log("fetching -- ", taskName, debouncedTaskName);
      fetchTasks();
    }
  }, [debouncedTaskName]);

  const handleTaskNameChange = (value: string) => {
    setTaskName(value);
  };

  const columns: Column<TaskCreations>[] = [
    {
      title: "Task name",
      dataIndex: "taskName",
      key: "taskName"
    },
    {
      title: "Class name",
      dataIndex: "className",
      key: "className",
      render: (className: string | undefined) => {
        if (!className) return "/a";
        const classNameParts = className.split(".");
        const classNameOnly = classNameParts[classNameParts.length - 1];

        return classNameOnly;
      }
    },
    {
      title: "Next runtime",
      dataIndex: "nextRunTime",
      key: "nextRunTime",
      render: (nextRunTime: string | undefined) =>
        nextRunTime ? dayjs(nextRunTime).format("YYYY-MM-DD HH:mm:ss") : ""
    },
    {
      title: "Last runtime",
      dataIndex: "lastRunTime",
      key: "lastRunTime",
      render: (lastRunTime: string | undefined) =>
        lastRunTime ? dayjs(lastRunTime).format("YYYY-MM-DD HH:mm:ss") : "N/A"
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      render: (value: string) => (
        <span className={`tag ${statusMap[value]}`}>{value}</span>
      )
    },
    {
      title: "",
      render: (_: unknown, task: TaskCreations) => {
        const options: ContextOption[] = [
          {
            label: "View Details",
            onClick: () => {
              setTaskOnView(task);
            }
          },
          {
            label: "Edit",
            onClick: () => {
              displayAddEditModal(task);
            }
          },

          ...(task.state === "HOLD"
            ? [
                {
                  label: "Resume",
                  onClick: () => handleUnholdClick(task)
                }
              ]
            : [
                {
                  label: "Hold",
                  onClick: () => handleHoldClick(task)
                }
              ]),

          {
            label: "Configuration",
            onClick: () => {
              handleConfigurationClick(task.taskName, task.id);
            }
          },
          {
            label: "Delete Task",
            onClick: () => {
              handleDeleteTasks(task.taskName);
            }
          },

          {
            label: "Generate",
            onClick: () => displayGenerateModal(task.taskName)
          },
          {
            label: "Task History",
            onClick: () => {
              handleHistoryClick(task.taskName, task.id);
            }
          },
          {
            label: "File History",
            onClick: () => {
              handleFileHistoryClick(task.taskName);
            }
          }
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      }
    }
  ];

  useEffect(() => {
    fetchTasks();
  }, [pagination.pageSize, pagination.pageNumber]);

  useEffect(() => {
    clearTimeout(timerRef);
    timerRef = setTimeout(() => {
      fetchTasks(1);
    }, 400);
  }, [resetFilter]);

  const resetFilters = () => {
    setState(undefined);
    setClassName(undefined);
    setTaskName("");
    setResetFilter("true");
  };

  useEffect(() => {
    const fetchClassNamesFromBackend = async () => {
      try {
        const response = await fetchClassNames();
        if (!response.error) {
          const classNamesData: ClassName[] = response.data || [];
          const options = classNamesData.map(className => ({
            label: className.className,
            value: className.fullClass
          }));
          setClassNamesOptions(options);
        }
      } catch (error) {
        console.error("Unable to fetch class names: ", error);
      }
    };
    fetchClassNamesFromBackend();
  }, []);

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">Task</h1>
      <div className="vertical-margin spaced">
        <div className={style.cont}>
          <div className={style.container}>
            <div className={style.cont1}>
              <div className="input-group ">
                <Select
                  options={statusOptions}
                  value={state || ""}
                  onSelect={value => setState(value as string)}
                  placeholder="Select State"
                  responsive
                />
              </div>

              <div className="input-group ">
                <Select
                  options={classNamesOptions}
                  value={className || ""}
                  onSelect={value => setClassName(value as string)}
                  placeholder="Select Class name"
                  responsive
                />
              </div>

              <div className="input-group ">
                <Input
                  value={taskName}
                  onChange={handleTaskNameChange}
                  placeholder="Select Task Name"
                  responsive
                />
              </div>
            </div>
          </div>
        </div>

        {(state || className || taskName) && (
          <Button type="accent" size="small" onClick={resetFilters}>
            RESET FILTERS
          </Button>
        )}
      </div>

      <div className="vertical-margin flex end">
        <Button onClick={() => displayAddEditModal(null)}>Create Task</Button>
        <span style={{ marginLeft: "8px" }} />
        <Button onClick={displayUploadModal}>Upload File</Button>
      </div>

      <Table
        dataSource={tasks || []}
        columns={columns}
        pagination={pagination}
        onPageChange={pageNumber =>
          setPagination(prev => ({ ...prev, pageNumber }))
        }
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
        loading={loading}
        className="smaller"
      />

      <TaskViewModal
        task={taskOnView}
        cancel={() => setTaskOnView(null)}
        refresh={fetchTasks}
        visible={Boolean(taskOnView)}
      />

      <AddEditModal
        visible={showAddEditModal}
        taskOnEdit={taskOnEdit}
        cancel={dismissAddEditModal}
        refresh={stayOnPage =>
          fetchTasks(stayOnPage ? pagination.pageNumber : 1)
        }
        taskCreation={taskCreate}
      />

      <GenerateModal
        visible={showGenerateModal}
        taskName={generateTaskName}
        month={selectedMonth}
        year={selectedYear}
        setMonth={setSelectedMonth}
        setYear={setSelectedYear}
        selectedDays={selectedDays}
        setSelectedDays={setSelectedDays}
        cancel={dismissGenerateModal}
        notify={notify}
        refresh={stayOnPage =>
          fetchTasks(stayOnPage ? pagination.pageNumber : 1)
        }
      />

      <FileUploadModal
        visible={showUploadModal}
        cancel={dismissUploadModal}
        taskClass={uploadTaskClass}
        setTaskClass={setUploadTaskClass}
        inheritedModule={uploadInheritedModule}
        setInheritedModule={setUploadInheritedModule}
        filesToUpload={filesToUpload}
        setFilesToUpload={setFilesToUpload}
        notify={notify}
        onUploadSuccess={fetchTasks}
      />
    </main>
  );
};

const blankTask: TaskCreate = {
  taskName: "",
  className: "",
  nextRunTime: "",
  intervalHour: 0,
  nextRunTimee: "",
  nextRunDate: "",
  inheritedModule: "",
  endTime: "",
  endDate: "",
  endTimee: ""
};

const AddEditModal: FunctionComponent<
  ModalProps & {
    taskOnEdit: TaskCreations | null;
    refresh: (stayOnPage: boolean) => void;
    taskCreation: TaskCreations[];
    cancel: () => void;
  }
> = ({ visible, cancel, taskOnEdit, refresh, taskCreation }) => {
  const [formData, setFormData] = useState<TaskCreate>({
    ...blankTask,
    ...taskOnEdit,
    nextRunTimee: taskOnEdit
      ? dayjs(taskOnEdit.nextRunTime).format("HH:mm:ss")
      : "",
    nextRunDate: taskOnEdit
      ? dayjs(taskOnEdit.nextRunTime).format("YYYY-MM-DD")
      : "",
    endTimee: taskOnEdit ? dayjs(taskOnEdit.endTime).format("HH:mm:ss") : "",
    endDate: taskOnEdit ? dayjs(taskOnEdit.endTime).format("YYYY-MM-DD") : "",
    inheritedModule: ""
  });

  const [isSaving, setIsSaving] = useState(false);
  const [classNames, setClassNames] = useState<ClassName[]>([]);
  const [inheritedModules, setInheritedModules] = useState<string[]>([]);

  const { notify } = useContext(LayoutContext);

  const handleChange = (key: keyof TaskCreate, value: unknown) => {
    setFormData(prevFormData => ({ ...prevFormData, [key]: value }));
  };

  const handleClassNameSelect = async (value: string) => {
    handleChange("className", value);

    handleChange("inheritedModule", "");

    try {
      const response = await fetchInheritedModules(value);
      if (!response.error) {
        setInheritedModules(response.data || []);
      }
    } catch (error) {
      console.error("Unable to fetch inherited modules: ", error);
    }
  };

  useEffect(() => {
    setFormData(
      taskOnEdit ? { ...blankTask, ...taskOnEdit } : { ...blankTask }
    );
  }, [visible, taskOnEdit]);

  useEffect(() => {
    const fetchData = async () => {
      const options = await fetchClassNames();
      if (!options.error) {
        const classNamesData: ClassName[] = options.data || [];
        setClassNames(classNamesData);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (visible && taskOnEdit) {
      const fetchInheritedModulesForEdit = async () => {
        if (taskOnEdit.className) {
          try {
            const response = await fetchInheritedModules(taskOnEdit.className);
            if (!response.error) {
              setInheritedModules(response.data || []);
            }
          } catch (error) {
            console.error("Unable to fetch inherited modules: ", error);
          }
        }
      };
      fetchInheritedModulesForEdit();
    }
  }, [visible, taskOnEdit]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setIsSaving(true);
    const formattedNextRunTime = dayjs(
      `${formData.nextRunDate} ${formData.nextRunTimee}`
    ).format("YYYY-MM-DD HH:mm:ss");

    const formattedEndTime = dayjs(
      `${formData.endDate} ${formData.endTimee}`
    ).format("YYYY-MM-DD HH:mm:ss");

    let id: number | undefined;

    if (taskOnEdit && typeof taskOnEdit.id === "number") {
      id = taskOnEdit.id;
    }

    if (id !== undefined) {
      const { error, message, data } = await updateTask(id, {
        ...formData,
        nextRunTime: formattedNextRunTime,
        endTime: formattedEndTime
      });

      if (error) {
        notify(
          "error",
          `Unable to update task: ${message}Error data:${JSON.stringify(
            data,
            null,
            2
          )}`
        );
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully updated Task");
    } else {
      const { error, message, data } = await createTask({
        ...formData,
        nextRunTime: formattedNextRunTime,
        endTime: formattedEndTime
      });

      if (error) {
        notify(
          "error",
          `Unable to create task: ${message}\nError data:\n${JSON.stringify(
            data,
            null,
            2
          ).replace(/</g, "&lt;br&gt;")}`
        );
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully created Task");
    }
    cancel?.();
    refresh(false);
    setIsSaving(false);
  };

  useEffect(() => {
    if (!isCreateOperation && taskOnEdit) {
      setFormData({
        taskName: taskOnEdit.taskName,
        className: taskOnEdit.className || "",
        intervalHour: taskOnEdit.intervalHour
          ? String(taskOnEdit.intervalHour)
          : "",
        inheritedModule: taskOnEdit ? taskOnEdit.inheritedModule : "",
        nextRunTime: taskOnEdit.nextRunTime || "",
        nextRunTimee: dayjs(taskOnEdit.nextRunTime).format("HH:mm:ss"),
        nextRunDate: dayjs(taskOnEdit.nextRunTime).format("YYYY-MM-DD"),
        endTime: taskOnEdit.endTime || "",
        endTimee: dayjs(taskOnEdit.endTime).format("HH:mm:ss"),
        endDate: dayjs(taskOnEdit.endTime).format("YYYY-MM-DD")
      });
    }
  }, [visible, taskOnEdit]);

  const isCreateOperation = taskOnEdit === null;

  return (
    <Modal cancel={cancel} visible={visible} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Task Name</span>
          <Input
            value={formData.taskName}
            onChange={value => handleChange("taskName", value)}
            placeholder="Enter task name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Task class</span>
            {isCreateOperation && <span className={styles.asterisk}>***</span>}
          </div>
          <Select
            value={formData.className || ""}
            onSelect={(value: any) => handleClassNameSelect(value)}
            placeholder="Select className"
            options={classNames.map(className => ({
              label: className.className,
              value: className.fullClass
            }))}
            disabled={!taskCreation}
            // dropdownOnTop
            responsive
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Inherited module</span>
          </div>
          <Select
            value={formData.inheritedModule || ""}
            onSelect={(value: any) => handleChange("inheritedModule", value)}
            placeholder="Select inherited module"
            options={inheritedModules.map(inheritedModule => ({
              label: inheritedModule,
              value: inheritedModule
            }))}
            disabled={!formData.className || !taskCreation}
            // dropdownOnTop
            responsive
          />
        </div>

        <div className="input-group">
          <span className="question">Next Runtime</span>
          <Input
            value={formData.nextRunDate}
            type="date"
            onChange={value => handleChange("nextRunDate", value)}
            responsive
            required
          />

          <Input
            value={formData.nextRunTimee}
            type="time"
            onChange={value => handleChange("nextRunTimee", value)}
            responsive
            required
            step="2"
          />
        </div>

        <div className="input-group">
          <span className="question">End Time</span>
          <Input
            value={formData.endDate}
            type="date"
            onChange={value => handleChange("endDate", value)}
            responsive
            required
          />

          <Input
            value={formData.endTimee}
            type="time"
            onChange={value => handleChange("endTimee", value)}
            responsive
            required
            step="2"
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Interval hour</span>
            {isCreateOperation && <span className={styles.asterisk}>***</span>}
          </div>
          <Select
            value={formData.intervalHour || ""}
            onSelect={(value: any) => handleChange("intervalHour", value)}
            placeholder="Select interval hour"
            options={getOptionsFromArray([
              "1",
              "2",
              "3",
              "4",
              "5",
              "6",
              "7",
              "8",
              "9",
              "10",
              "11",
              "12",
              "13",
              "14",
              "15",
              "16",
              "17",
              "18",
              "19",
              "20",
              "21",
              "22",
              "23",
              "24"
            ])}
            disabled={!taskCreation}
            dropdownOnTop
            responsive
          />
        </div>

        <br />
        <div className="flex spaced end vertical-margin">
          <Button type="accent" onClick={() => cancel?.()}>
            Cancel
          </Button>
          <Button buttonType="submit" loading={isSaving}>
            {taskOnEdit ? "Edit Task" : "Create Task"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface TaskEditModalProps {
  task: TaskCreations | null;
  cancel: () => void;
  refresh: () => void;
  visible: boolean;
}

const TaskViewModal: FunctionComponent<TaskEditModalProps> = ({
  task: _task,
  cancel,
  visible,
  refresh
}) => {
  const [loading] = useState(false);
  const [tasks, setTasks] = useState<TaskCreations | null>(_task);

  useEffect(() => {
    setTasks(_task);
  }, [_task]);

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>Task Name:</span>
            <span className={styles.value}>{tasks?.taskName || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Class Name:</span>
            <span className={styles.value}>{tasks?.className || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>In Progress:</span>
            <span className={styles.value}>{tasks?.state || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Inherited Module:</span>
            <span className={styles.value}>
              {tasks?.inheritedModule || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Interval Hour:</span>
            <span className={styles.value}>{tasks?.intervalHour || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Last Runtime:</span>
            <span className={styles.value}>{tasks?.lastRunTime || "N/A"}</span>
          </div>
          {/* <div className={styles.detail}>
            <span className={styles.key}>Retry Number:</span>
            <span className={styles.value}>{tasks?.retryNum || "N/A"}</span>
          </div> */}
          <div className={styles.detail}>
            <span className={styles.key}>Next Runtime:</span>
            <span className={styles.value}>{tasks?.nextRunTime || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>End Time:</span>
            <span className={styles.value}>{tasks?.endTime || "N/A"}</span>
          </div>
        </section>
      )}
    </Modal>
  );
};

const currentYear = dayjs().year();

const yearOptions = Array.from({ length: currentYear - 2020 + 1 }, (_, i) => ({
  label: (2020 + i).toString(),
  value: 2020 + i
}));

const monthOptions = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 }
];
const dayOptions = Array.from({ length: 31 }, (_, i) => ({
  label: (i + 1).toString(),
  value: i + 1
}));

const executeTask = async (
  formData: TaskExecute,
  cancel: () => void,
  notify: any,
  refresh: (refresh: boolean) => void,
  setIsSaving: (isSaving: boolean) => void,
  setSelectedDays: (days: number[]) => void
) => {
  setIsSaving(true);

  const payload: TaskExecute = {
    ...formData,
    dayFrom: formData.dayFrom ?? 0,
    dayTo: formData.dayTo ?? 0
  };

  const { error, message, data } = await excuteTasks(payload);

  if (error) {
    const formattedData = Array.isArray(data) ? data.join(", ") : data;
    notify(
      "error",
      `Unable to execute task: ${message}, ${
        formattedData?.replace(/</g, "&lt;br&gt;") || ""
      }`
    );
    setIsSaving(false);
    return;
  }

  notify("success", "Task execution in progress");

  setSelectedDays([]);

  cancel();
  refresh(false);
  setIsSaving(false);
};

const GenerateModal: FunctionComponent<{
  visible: boolean;
  taskName: string;
  month: number | undefined;
  year: number | undefined;
  setMonth: (month: number | undefined) => void;
  setYear: (year: number | undefined) => void;
  selectedDays: number[];
  setSelectedDays: (days: number[]) => void;
  cancel: () => void;
  notify: (
    type: "success" | "error" | "info",
    message: string,
    duration?: number
  ) => void;
  refresh: (refresh: boolean) => void;
}> = ({
  visible,
  taskName,
  month,
  year,
  setMonth,
  setYear,
  selectedDays,
  setSelectedDays,
  cancel,
  notify,
  refresh
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmitClick = () => {
    const formData: TaskExecute = {
      taskName,
      year,
      month,
      days: selectedDays
    };

    executeTask(
      formData,
      cancel,
      notify,
      refresh,
      setIsSaving,
      setSelectedDays
    );
  };

  return (
    <Modal visible={visible} cancel={cancel}>
      <div>
        <div>
          <div style={{ marginBottom: "1rem" }}>
            <h4 style={{ marginBottom: "1rem" }}>Task Name</h4>
            <Input type="text" value={taskName} disabled responsive />
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <h4 style={{ marginBottom: "1rem" }}>Year</h4>
            <Select
              responsive
              options={yearOptions}
              value={year}
              onSelect={value => setYear(value as number)}
              placeholder="Select Year"
            />
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <h4 style={{ marginBottom: "1rem" }}>Month</h4>
            <Select
              options={monthOptions}
              value={month}
              onSelect={value => setMonth(value as number)}
              placeholder="Select Month"
              responsive
              dropdownOnTop
            />
          </div>

          <div style={{ marginBottom: "2rem" }}>
            <h4 style={{ marginBottom: "1rem" }}>Days</h4>
            <Select
              multiple
              options={dayOptions}
              value={selectedDays}
              onSelect={selected => {
                const validSelectedDays = Array.isArray(selected)
                  ? (selected as number[]).filter(
                      (item: unknown): item is number =>
                        typeof item === "number"
                    )
                  : [];

                setSelectedDays(validSelectedDays);
              }}
              placeholder="Select Days"
              responsive
              dropdownOnTop
            />
          </div>

          <Button
            type="primary"
            onClick={handleSubmitClick}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Submit"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TaskCreation;
